'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import store from 'AdsUI/redux-store';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import PortalIdParser from 'PortalIdParser';
import BannerTypes from 'AdsUI/lib/BannerTypes';
import UIAlert from 'UIComponents/alert/UIAlert';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import UIButton from 'UIComponents/button/UIButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import FacebookToSChangedBanner from 'AdsUI/components/facebook/FacebookToSChangedBanner';
import H5 from 'UIComponents/elements/headings/H5';
import I18n from 'I18n';
import { InactiveMessage } from 'ads-account-connection/components/AccountPermissionsMessaging';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import LeadSyncMissingEmailBanner from './LeadSyncMissingEmailBanner';
import { COUPON_LEARN_MORE } from 'AdsUI/lib/Constants';
import KnowledgeBaseLink from 'AdsUI/components/KnowledgeBaseLink';
import { KBArticles, KBArticleSections } from 'AdsUI/lib/Constants';
import { openEventPanel } from 'AdsUI/actions/EventSettingsActions';
import FacebookCapiReplaceEventsBanner from 'AdsUI/components/events/FacebookCapiReplaceEventsBanner';
const settingsUrl = `/settings/${PortalIdParser.get()}/marketing/ads`;

const multiExpirationBanner = type => {
  const MultiExpirationBanner = ({
    users,
    onClose
  }) => {
    const userList = users.map(user => {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `expiration.multi.${type}.bullet`,
        options: {
          network: I18n.text(user.network.toLowerCase()),
          userName: user.userName,
          expirationDate: I18n.moment(user.expiresAt).format('L')
        }
      }, `${user.network}${user.userId}`);
    });
    const alertType = type === 'alert' ? 'danger' : 'warning';
    return /*#__PURE__*/_jsxs(UIAlert, {
      type: alertType,
      closeable: true,
      onClose: onClose,
      children: [/*#__PURE__*/_jsx(H5, {
        className: "m-bottom-2 m-top-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "errors.permissions.headsUp"
        })
      }), /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `expiration.multi.${type}.message`,
        options: {
          settingsUrl
        }
      }), /*#__PURE__*/_jsx(UIList, {
        styled: true,
        childClassName: "m-y-2",
        children: userList
      })]
    });
  };

  return MultiExpirationBanner;
};

const AdBlockDetectedBanner = () => /*#__PURE__*/_jsx(UIAlert, {
  type: "danger",
  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "adblockDetection.title"
  }),
  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: "adblockDetection.description"
  })
});

const ExpirationWarningBanner = ({
  onClose,
  userName,
  expirationDate,
  network
}) => /*#__PURE__*/_jsx(UIAlert, {
  type: "warning",
  closeable: true,
  onClose: onClose,
  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: `expiration.${network}.warning.title`,
    options: {
      userName,
      expirationDate
    }
  }),
  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `expiration.${network}.warning.message`,
    options: {
      settingsUrl
    }
  })
});

const ExpirationAlertBanner = ({
  onClose,
  userName,
  network
}) => /*#__PURE__*/_jsx(UIAlert, {
  type: "danger",
  closeable: true,
  onClose: onClose,
  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "errors.permissions.headsUp"
  }),
  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: "errors.permissions.expired",
    options: {
      settingsUrl,
      userName,
      network: I18n.text(network.toLowerCase())
    }
  })
});

const AdWordsCreatedSearchCampaignBanner = ({
  onClose
}) => /*#__PURE__*/_jsx(UIAlert, {
  type: "success",
  closeable: true,
  onClose: onClose,
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "adWordsCampaignCreation.creationSuccess",
    options: {}
  })
});

const AdWordsCreatedMobileCampaignBanner = ({
  onClose
}) => /*#__PURE__*/_jsx(UIAlert, {
  type: "success",
  closeable: true,
  onClose: onClose,
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "adWordsCampaignCreation.creationSuccess",
    options: {}
  })
});

const AppPermissionsAlertBanner = ({
  onClose
}) => {
  const LearnMoreLink = () => /*#__PURE__*/_jsx(KnowledgeBaseLink, {
    kbArticle: KBArticles.FACEBOOK_ADS_TROUBLESHOOT,
    kbSection: KBArticleSections[KBArticles.FACEBOOK_ADS_TROUBLESHOOT]['USER_PERMISSIONS'],
    context: "fb-ads-user-permission-alert"
  });

  const UpdatePermissionsLink = props => /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(UILink, Object.assign({
      href: settingsUrl
    }, props))
  });

  return /*#__PURE__*/_jsx(UIAlert, {
    closeable: true,
    onClose: onClose,
    titleText: I18n.text('missingPermissions.title'),
    type: "danger",
    children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "missingPermissions.message_jsx",
      elements: {
        LearnMoreLink,
        UpdatePermissionsLink
      }
    })
  });
};

const InactiveAccountBanner = ({
  adAccount
}) => {
  useEffect(() => {
    usageTracker.track('errorDisplay', {
      type: 'inactive',
      form: 'banner'
    });
  }, []);
  return /*#__PURE__*/_jsx(UIAlert, {
    closeable: false,
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "errors.permissions.headsUp"
    }),
    type: "danger",
    children: /*#__PURE__*/_jsx(InactiveMessage, {
      adAccount: adAccount,
      onClick: () => {
        usageTracker.track('errorAction', {
          type: 'inactive',
          action: 'clicked kb link'
        });
      }
    })
  });
};

const LEAD_SYNC_SETTINGS_URL = `/settings/${PortalIdParser.get()}/marketing/ads/leadSyncing`;

const handleLeadSyncErrorBannerClick = () => {
  usageTracker.track('leadSyncingAction', {
    action: 'view-errors',
    context: 'error-banner'
  });
};

const handleLeadSyncWarningBannerClick = () => {
  usageTracker.track('leadSyncingAction', {
    action: 'view-warnings',
    context: 'warning-banner'
  });
};

const LeadSyncErrorBanner = ({
  onClose
}) => /*#__PURE__*/_jsx(UIAlert, {
  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "leadSyncingErrorBanner.title"
  }),
  type: "danger",
  closeable: true,
  onClose: onClose,
  children: /*#__PURE__*/_jsxs("span", {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "leadSyncingErrorBanner.note"
    }), ' ', /*#__PURE__*/_jsx(UILink, {
      href: LEAD_SYNC_SETTINGS_URL,
      external: true,
      onClick: handleLeadSyncErrorBannerClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "leadSyncingErrorBanner.action"
      })
    })]
  })
});

const LeadSyncWarningBanner = ({
  onClose
}) => /*#__PURE__*/_jsx(UIAlert, {
  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "leadSyncingWarningBanner.title"
  }),
  type: "warning",
  closeable: true,
  onClose: onClose,
  children: /*#__PURE__*/_jsxs("span", {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "leadSyncingWarningBanner.note"
    }), ' ', /*#__PURE__*/_jsx(UILink, {
      href: LEAD_SYNC_SETTINGS_URL,
      external: true,
      onClick: handleLeadSyncWarningBannerClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "leadSyncingWarningBanner.action"
      })
    })]
  })
});

export const CouponBanner = ({
  onClose,
  onHide
}) => {
  return /*#__PURE__*/_jsx(UIAlert, {
    closeable: true,
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "coupon.banner.title"
    }),
    type: "success",
    onClose: () => {
      if (onClose) {
        onClose();
      }

      onHide();
    },
    children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "coupon.banner.message_jsx",
      options: {
        href: COUPON_LEARN_MORE,
        external: true
      },
      elements: {
        Link: UILink
      }
    })
  });
};

const trackOpenEventPanel = ({
  viewEventPanel
}) => {
  if (viewEventPanel) {
    usageTracker.track('modifyEvent', {
      action: 'Opened create event panel from info banner'
    });
  } else {
    usageTracker.track('modifyEvent', {
      action: 'Closed info banner'
    });
  }
};

const LearnMoreEnhancedConversion = () => /*#__PURE__*/_jsx(KnowledgeBaseLink, {
  kbArticle: KBArticles.SYNC_GOOGLE_FORM_SUBMISSION_EVENTS,
  context: "sync-google-form-ads-converstion-events",
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "enhancedConversionBanner.learnMore"
  })
});

export const EnhancedConversionBanner = ({
  onClose
}) => {
  return /*#__PURE__*/_jsx(UIAlert, {
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "enhancedConversionBanner.title"
    }),
    type: "info",
    closeable: true,
    onClose: () => {
      trackOpenEventPanel({
        viewEventPanel: false
      });
      onClose();
    },
    children: /*#__PURE__*/_jsxs(UIButtonWrapper, {
      children: [/*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "enhancedConversionBanner.note"
      }), /*#__PURE__*/_jsx(LearnMoreEnhancedConversion, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "enhancedConversionBanner.password"
      }), /*#__PURE__*/_jsx(UIButton, {
        size: "extra-small",
        use: "tertiary",
        onClick: () => {
          store.dispatch(openEventPanel());
          trackOpenEventPanel({
            viewEventPanel: true
          });
          onClose();
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "enhancedConversionBanner.tryItNow"
        })
      })]
    })
  });
};
export default {
  [BannerTypes.ADBLOCK_DETECTED]: AdBlockDetectedBanner,
  [BannerTypes.EXPIRATION_WARNING]: ExpirationWarningBanner,
  [BannerTypes.EXPIRATION_ALERT]: ExpirationAlertBanner,
  [BannerTypes.MULTI_EXPIRATION_WARNING]: multiExpirationBanner('warning'),
  [BannerTypes.MULTI_EXPIRATION_ALERT]: multiExpirationBanner('alert'),
  [BannerTypes.INACTIVE_ACCOUNT]: InactiveAccountBanner,
  [BannerTypes.ADWORDS_CREATED_SEARCH_CAMPAIGN]: AdWordsCreatedSearchCampaignBanner,
  [BannerTypes.ADWORDS_CREATED_MOBILE_CAMPAIGN]: AdWordsCreatedMobileCampaignBanner,
  [BannerTypes.FACEBOOK_AUDIENCE_TOS_CHANGED]: FacebookToSChangedBanner,
  [BannerTypes.APP_PERMISSIONS_ALERT]: AppPermissionsAlertBanner,
  [BannerTypes.LEAD_SYNC_MISSING_EMAIL]: LeadSyncMissingEmailBanner,
  [BannerTypes.LEAD_SYNC_ERROR_ALERT]: LeadSyncErrorBanner,
  [BannerTypes.LEAD_SYNC_WARNING_ALERT]: LeadSyncWarningBanner,
  [BannerTypes.COUPONS]: CouponBanner,
  [BannerTypes.ENHANCED_CONVERSIONS]: EnhancedConversionBanner,
  [BannerTypes.FACEBOOK_CAPI_REPLACE_EVENTS]: FacebookCapiReplaceEventsBanner
};